import { AxiosError } from "axios";
import apiRekognition from "../config/apiRekognition";

interface Alias {
	Name?: string;
}

interface Category {
	Name?: string;
}

// Type returned from AWS Rekognition API.
interface RekognitionItem {
	Aliases?: Alias[];
	Categories?: Category[];
	Confidence?: number;
	Name?: string;
}

const readImageAsBase64 = (file: File): Promise<string> => {
	return new Promise((resolve, reject) => {
		const reader = new FileReader();

		reader.onload = () => {
			const result = reader.result as string;
			// Remove the data:image/jpeg;base64, or similar prefix.
			const base64 = result.split(",")[1];
			resolve(base64);
		};

		reader.onerror = reject;

		reader.readAsDataURL(file);
	});
};

export async function compressImage(file: File, maxWidth = 1024, quality = 0.8): Promise<string> {
	const supportedTypes = ["image/jpeg", "image/png"];
	if (!supportedTypes.includes(file.type)) {
		throw new Error(`Unsupported file type: ${file.type}. Only JPEG and PNG are supported.`);
	}

	console.log(`Original file size: ${(file.size / 1024).toFixed(2)} KB`);

	const imageBitmap = await createImageBitmap(file);

	const canvas = document.createElement("canvas");
	const scale = Math.min(maxWidth / imageBitmap.width, 1); // scale down if needed
	canvas.width = imageBitmap.width * scale;
	canvas.height = imageBitmap.height * scale;

	const ctx = canvas.getContext("2d");
	if (!ctx) throw new Error("Could not get canvas context");

	ctx.drawImage(imageBitmap, 0, 0, canvas.width, canvas.height);

	// Always convert to JPEG for better compression and AWS Rekognition compatibility
	const compressedBase64 = canvas.toDataURL("image/jpeg", quality);
	const base64Data = compressedBase64.split(",")[1];
	const compressedSizeInBytes = (base64Data.length * 3) / 4;
	console.log(`Compressed base64 size: ${(compressedSizeInBytes / 1024).toFixed(2)} KB`);

	return compressedBase64;
}

export const sendToApi = async (file?: File): Promise<any> => {
	try {
		if (file) {
			//const base64 = await readImageAsBase64(file);
			const compressedBase64 = await compressImage(file, 1024, 0.8); // max width 1024px, quality 80%

			// Remove the "data:image/jpeg;base64," prefix
			const base64 = compressedBase64.split(",")[1];

			const response = await apiRekognition.post("/rekognition/analyzephoto", {
				photo: base64,
			});

			const mapRekognitionData = (data: RekognitionItem[] = []) => {
				return data.map((item) => ({
					aliases: Array.isArray(item.Aliases) ? item.Aliases.map((alias) => alias?.Name ?? "") : [],
					categories: Array.isArray(item.Categories)
						? item.Categories.map((category) => category?.Name ?? "")
						: [],
					confidence: typeof item.Confidence === "number" ? item.Confidence : null,
					name: item.Name ?? "",
				}));
			};

			console.log("Rekognition response", response.data);

			const rekognitionResponse = mapRekognitionData(response.data);

			return rekognitionResponse;
		}
		return null;
	} catch (error) {
		console.error("Error sending image to API:", error);

		if (error instanceof AxiosError) {
			if (error.code == "ERR_NETWORK") {
				return "network-error";
			}
		}

		throw new Error("Failed to process image");
	}
};
